/**
 * Layer A: the wire contract.
 *
 * What the server puts on the wire. Discriminated by the SSE `event:` line, not
 * by a field inside `data`. Payload fields are snake_case, exactly as published.
 *
 * Provenance. Every name and field shape in this file was read from the
 * declarations and the bundled parser published in `matilda-agent-sdk` version
 * 0.1.0 on the npm registry. That package is UNLICENSED. It is not a
 * dependency, no copy of it lives in this repository, and no code from it
 * appears here. The local reference copy is kept outside the repository
 * entirely and `dep-lint` fails the build if any of it appears inside.
 *
 *   names and shapes   dist/index.d.ts lines 227 to 608
 *   parser behaviour   dist/index.js lines 136 to 338
 *
 * See docs/PROTOCOL.md for the full provenance table, including which rows were
 * checked against a local artifact and which rest on a citation.
 */

/**
 * The default event name. A blank line resets the current event name to this
 * value, so a frame that carries no `event:` line arrives as `message`. A
 * conforming client ignores those. // observed
 */
export const DEFAULT_EVENT_NAME = 'message';

/**
 * A `data:` line whose content is exactly this string is a sentinel. It is
 * skipped before dispatch and before cursor delivery, so it advances nothing.
 * // observed
 */
export const DONE_SENTINEL = '[DONE]';

/**
 * The fifteen wire event names, in the order they are published.
 * // observed
 */
export const WIRE_EVENT_NAMES = [
  'stream_init',
  'token',
  'thinking',
  'tool_start',
  'tool_result',
  'tool_progress',
  'generation_status',
  'client_tool_call',
  'safety_replace',
  'usage',
  'status',
  'replace',
  'truncated',
  'done',
  'error',
] as const;

// observed
export type WireEventName = (typeof WIRE_EVENT_NAMES)[number];

const WIRE_EVENT_NAME_SET: ReadonlySet<string> = new Set<string>(WIRE_EVENT_NAMES);

/** True when the `event:` line names an event this contract describes. */
export function isWireEventName(name: string): name is WireEventName {
  return WIRE_EVENT_NAME_SET.has(name);
}

/**
 * Values the reference parser substitutes when a field is absent or the wrong
 * type. Every one of these is exercised by a dedicated adapter test, because
 * these are the details a hand written client gets wrong.
 * // observed
 */
export const WIRE_DEFAULTS = {
  /** `status.state` when absent or not a string. */
  queueState: 'queued',
  /** `truncated.reason` when absent or not a string. */
  truncatedReason: 'max_tokens',
  /** `status.position` and `status.estimated_wait_seconds` when absent. */
  queueNumber: 0,
  /** `usage.output_tokens` when absent or not coercible. */
  outputTokens: 0,
} as const;

// observed
export interface StreamInitData {
  stream_id: string;
}

// observed
export interface TokenData {
  content: string;
}

/**
 * Declared with a required `content` field, but the reference parser reads none
 * of it and emits a status change only. The field is modelled so the server can
 * emit a faithful frame. The adapter ignores the value.
 * // observed
 */
export interface ThinkingData {
  content: string;
}

// observed
export interface GenerationStatusData {
  phase: string;
}

/**
 * `input` takes precedence over `args`. `args` may arrive as a JSON encoded
 * string, in which case it is parsed, and dropped when that parse fails.
 * `output` falls back to `result`.
 * // observed
 */
export interface ToolStartData {
  tool: string;
  input?: string;
  output?: string;
  result?: string;
  args?: Record<string, unknown> | string;
}

// observed
export interface ToolProgressData {
  tool: string;
  message: string;
}

/**
 * Declared as an enum of `success` and `error`, but the reference parser treats
 * the value as `success` only when it is exactly the string `success`, and
 * everything else, including a missing field, as an error. The type here is
 * widened to `string` so the server can emit the non conforming case that the
 * `tool_result` adapter test asserts on.
 * // observed
 */
export interface ToolResultData {
  tool: string;
  status: string;
  input?: string;
  output?: string;
  result?: string;
}

// observed
export interface ClientToolCallData {
  id?: string;
  name: string;
  args?: Record<string, unknown>;
}

/**
 * `message` takes precedence over `content`. `categories` is filtered to
 * strings only and defaults to the empty array.
 * // observed
 */
export interface SafetyReplaceData {
  message?: string;
  content?: string;
  categories?: string[];
}

/**
 * Declared as an empty object. Distinct from `safety_replace`: this carries no
 * replacement text and no categories, and it means a bare clear.
 * // observed
 */
export type ReplaceData = Record<string, never>;

// observed
export interface TruncatedData {
  reason?: string;
}

/**
 * snake_case is preserved from the wire on purpose. This is how the shape is
 * published, and matching it exactly is the point.
 * // observed
 */
export interface UsageData {
  output_tokens: number;
  context_pct?: number;
  context_messages_trimmed?: number;
  context_budget_tokens?: number;
}

// observed
export interface StatusData {
  state: string;
  position?: number;
  estimated_wait_seconds?: number;
}

/** All four fields are optional and all four are ignored by the reference parser. */
// observed
export interface DoneData {
  completion_tokens?: number;
  reason?: string;
  degraded?: boolean;
  input_refusal?: boolean;
}

/**
 * The message field is named `error`, not `message`. `code` is validated
 * against a fixed set and falls back to `unknown`. When the payload is not
 * valid JSON the raw `data` line becomes the message.
 * // observed
 */
export interface ErrorData {
  code?: string;
  error: string;
}

/**
 * A frame as it appears on the wire, discriminated by the `event:` line.
 * // observed
 */
export type WireEvent =
  | { event: 'stream_init'; data: StreamInitData }
  | { event: 'token'; data: TokenData }
  | { event: 'thinking'; data: ThinkingData }
  | { event: 'tool_start'; data: ToolStartData }
  | { event: 'tool_result'; data: ToolResultData }
  | { event: 'tool_progress'; data: ToolProgressData }
  | { event: 'generation_status'; data: GenerationStatusData }
  | { event: 'client_tool_call'; data: ClientToolCallData }
  | { event: 'safety_replace'; data: SafetyReplaceData }
  | { event: 'usage'; data: UsageData }
  | { event: 'status'; data: StatusData }
  | { event: 'replace'; data: ReplaceData }
  | { event: 'truncated'; data: TruncatedData }
  | { event: 'done'; data: DoneData }
  | { event: 'error'; data: ErrorData };

/** The payload type carried by a given wire event name. */
export type WireDataFor<N extends WireEventName> = Extract<WireEvent, { event: N }>['data'];
